import React, { useState, useRef, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import _get from "lodash/get";

import propTypes from "../../../../../constants/propTypes";
import { handleRequestError } from "../../../../../utils/formValidationUtils";
import { processUpdateRequestStatus } from "../../../../../utils/dataComponentUtils";
import { LoaderContext } from "../../../../ui/Loader";
import QuickEditSpecForm from "./QuickEditSpecForm";
import withPORevision, {
  LabeledTextContext,
} from "../../../../../withPORevision";
import PurchaseOrder from "../../../../../models/PurchaseOrder";
import {
  mapStateToProps,
  mapDispatchToProps,
  PODataComponentId,
} from "./connect";
import { isPOChangeDisabled } from "../../utils";
import { cleanCurrencyValue } from "../../../../../utils/currencyFormatter";
import { useIsProjectClosed } from "../../../../hooks/useIsProjectClosed";
import {
  needsSpecCategoryConfirmation,
  openSpecCategoryConfirmation,
} from "../../../Specs/specCategoryConfirmation";

// BW-7011 fix 2: when the confirmation dialog reopens Quick Edit, `formikActions` is null on the
// freshly mounted instance — the instance that actually submitted (and called setFormikActions)
// was unmounted the moment the confirmation dialog took the modal slot. There is no live Formik
// bag left to attach field errors to, so a generic, non-Formik message is the only channel left
// to tell the user their save failed.
const getGenericSaveErrorMessage = error => {
  const [firstError] = _get(error, "data.errors", []);
  return (
    (firstError && firstError.title) ||
    "There was a problem saving this spec. Please try again."
  );
};

const scrollToTop = ref => ref?.current?.scrollTo(0, 0);

const handleSaveSuccess = (
  {
    dataComponentId,
    purchaseOrder,
    nextSpec,
    clientId,
    ref,
    revisionValue,
    isSaveAndNext,
  },
  {
    closeModalDialog,
    setReload,
    performFindRequest,
    openQuickEditModalAction,
    destroyDataComponent,
    getNextSpecId,
    fetchPurchaseOrder,
  }
) => {
  if (isSaveAndNext) {
    scrollToTop(ref);
    openQuickEditModalAction({
      dataComponentId,
      spec: nextSpec,
      clientId,
      getNextSpecId,
    });
  } else {
    closeModalDialog();
    setReload(dataComponentId, true);
    destroyDataComponent(`${dataComponentId}Clone`);
  }

  if (purchaseOrder) {
    fetchPurchaseOrder(purchaseOrder.id, purchaseOrder.projectId);
  }

  if (revisionValue.isActive) {
    performFindRequest(
      PODataComponentId,
      purchaseOrder.id,
      {},
      { omitDefaultModifier: true }
    );
  }
};

const handleSaveError = (error, formikActions, showSnackNotificationAction) => {
  if (formikActions) {
    handleRequestError(error, formikActions);
    return;
  }
  showSnackNotificationAction(getGenericSaveErrorMessage(error));
};

export const processRequestStatus = ({
  dataComponentInfo: { dataComponent, prevDataComponent, dataComponentId },
  specInfo: { purchaseOrder, nextSpec, clientId, ref },
  callbacks,
  formikActions,
  revisionValue,
  isSaveAndNext,
}) => {
  processUpdateRequestStatus(prevDataComponent, dataComponent, {
    onSuccess: () =>
      handleSaveSuccess(
        {
          dataComponentId,
          purchaseOrder,
          nextSpec,
          clientId,
          ref,
          revisionValue,
          isSaveAndNext,
        },
        callbacks
      ),
    onError: error =>
      handleSaveError(
        error,
        formikActions,
        callbacks.showSnackNotificationAction
      ),
  });
};

const useProcessRequestStatus = ({
  cloneDataComponent,
  prevDataComponent,
  dataComponentId,
  purchaseOrder,
  nextSpec,
  clientId,
  ref,
  performFindRequest,
  closeModalDialog,
  setReload,
  openQuickEditModalAction,
  destroyDataComponent,
  getNextSpecId,
  fetchPurchaseOrder,
  showSnackNotificationAction,
  formikActions,
  revisionValue,
  isSaveAndNext,
}) => {
  useEffect(() => {
    processRequestStatus({
      dataComponentInfo: {
        dataComponent: cloneDataComponent,
        prevDataComponent,
        dataComponentId,
      },
      specInfo: { purchaseOrder, nextSpec, clientId, ref },
      callbacks: {
        performFindRequest,
        closeModalDialog,
        setReload,
        openQuickEditModalAction,
        destroyDataComponent,
        getNextSpecId,
        fetchPurchaseOrder,
        showSnackNotificationAction,
      },
      formikActions,
      revisionValue,
      isSaveAndNext,
    });
  }, [
    closeModalDialog,
    openQuickEditModalAction,
    destroyDataComponent,
    setReload,
    dataComponentId,
    cloneDataComponent,
    prevDataComponent,
    purchaseOrder,
    formikActions,
    revisionValue,
    performFindRequest,
    isSaveAndNext,
    nextSpec,
    clientId,
    getNextSpecId,
    fetchPurchaseOrder,
    showSnackNotificationAction,
    ref,
  ]);
};

const findPO = (initDataComponent, performFindRequest, purchaseOrderId) => {
  if (purchaseOrderId) {
    initDataComponent(
      PODataComponentId,
      PurchaseOrder,
      ["currentRevision.revisionActivities", "vendor"],
      "purchase-orders",
      true,
      undefined
    );
    performFindRequest(
      PODataComponentId,
      purchaseOrderId,
      {},
      { omitDefaultModifier: true }
    );
  }
};

// Quick Edit *is* the single Redux modal slot (opened via openQuickEditModalAction), so
// openSpecCategoryConfirmation's own openModalDialog call replaces it and unmounts the
// container that called it. From that container's point of view, "closing" the confirmation
// dialog means reopening Quick Edit with the row action's exact arguments, not calling the
// real closeModalDialog, which would just leave the modal slot empty. This covers both
// outcomes: Confirm (after the save is kicked off, so the freshly mounted container's
// processRequestStatus effect is there to observe it resolve) and Cancel (so the user lands
// back on Quick Edit instead of losing the modal entirely).
const useHandleSubmitWithConfirmation = ({
  purchaseOrder,
  spec,
  openModalDialog,
  openQuickEditModalAction,
  dataComponentId,
  clientId,
  getNextSpecId,
  handleSubmit,
  isSaveAndNextRef,
}) => {
  const reopenQuickEditModal = useCallback(() => {
    openQuickEditModalAction({
      dataComponentId,
      spec,
      clientId,
      getNextSpecId,
      // BW-7011 fix 4: carry Save & Next across the reopen — read from the ref (kept in sync
      // by setSaveAndNext below), not React state, since the click that set the state and
      // the unmount that discards it happen in the same commit.
      isSaveAndNext: isSaveAndNextRef.current,
    });
  }, [
    openQuickEditModalAction,
    dataComponentId,
    spec,
    clientId,
    getNextSpecId,
    isSaveAndNextRef,
  ]);

  return useCallback(
    (data, formikBag) => {
      if (
        !needsSpecCategoryConfirmation(
          purchaseOrder,
          spec.specCategoryId,
          data.specCategoryId
        )
      ) {
        return handleSubmit(data, formikBag);
      }

      openSpecCategoryConfirmation(
        { openModalDialog, closeModalDialog: reopenQuickEditModal },
        {
          onConfirm: () => handleSubmit(data, formikBag),
          // No formikBag here (BW-7011 fix 5): this container — and the Formik instance that
          // produced `formikBag` — is unmounted the instant openModalDialog above replaces the
          // single modal slot with the confirmation dialog, well before the user can act on it.
          // A resetForm call against it would only warn about updating state on an unmounted
          // component and change nothing; the reopened instance already mounts a clean form.
          currentCategoryId: spec.specCategoryId,
          submittedValues: data,
        }
      );
    },
    [purchaseOrder, spec, openModalDialog, reopenQuickEditModal, handleSubmit]
  );
};

const submit = async (
  cloneDataComponentId,
  data,
  spec,
  revisionValue,
  quickUpdateSpec
) => {
  const dataToSent = { ...data };
  if (dataToSent.priceCents) {
    dataToSent.priceCents = cleanCurrencyValue(dataToSent.priceCents * 100);
  }
  const params = revisionValue.isActive ? { update_po_revision: true } : {};
  quickUpdateSpec(cloneDataComponentId, spec.id, spec.projectId, dataToSent, {
    ...params,
    omitDefaultModifier: true,
    modifiers: [
      "withQuantityPrice",
      "withTotalQuantityPaid",
      "withTotalQuantityShipped",
      "withAssignedToBidGroup",
    ],
  });
};

export const QuickEditSpecContainer = ({
  cloneDataComponent,
  dataComponentId,
  revisionValue,
  purchaseOrder,
  purchaseOrderId,
  specId,
  projectId,
  clientId,
  nextSpecId,
  getNextSpecId,
  performFindRequest,
  openQuickEditModalAction,
  destroyDataComponent,
  quickUpdateSpec,
  initDataComponent,
  initQuickEditSpecForm,
  closeModalDialog,
  openModalDialog,
  setReload,
  spec,
  nextSpec,
  loading,
  loadingSave,
  fetchPurchaseOrder,
  initQuickEditSpec,
  cloneDataComponentId,
  showSnackNotificationAction,
  initialSaveAndNext,
}) => {
  const [formikActions, setFormikActions] = useState(null);
  const [prevDataComponent, setPrevDatacomponent] = useState(
    cloneDataComponent
  );
  const [isSaveAndNext, setIsSaveAndNextState] = useState(!!initialSaveAndNext);
  // BW-7011 fix 4: mirrors isSaveAndNext synchronously. setSaveAndNext (state) only takes effect
  // on the next render, but this container can unmount — reopened by the confirmation dialog —
  // in the very same commit as the click that called it, before that render happens. The ref is
  // read later by reopenQuickEditModal, at whatever point the user resolves the confirmation.
  const isSaveAndNextRef = useRef(!!initialSaveAndNext);
  const setSaveAndNext = useCallback(value => {
    isSaveAndNextRef.current = value;
    setIsSaveAndNextState(value);
  }, []);
  const [ref, setRef] = useState(null);

  useEffect(() => {
    initQuickEditSpec(specId, dataComponentId, cloneDataComponentId);
  }, [cloneDataComponentId, dataComponentId, initQuickEditSpec, specId]);

  useEffect(() => {
    setPrevDatacomponent(cloneDataComponent);
  }, [cloneDataComponent]);

  useProcessRequestStatus({
    cloneDataComponent,
    prevDataComponent,
    dataComponentId,
    purchaseOrder,
    nextSpec,
    clientId,
    ref,
    performFindRequest,
    closeModalDialog,
    setReload,
    openQuickEditModalAction,
    destroyDataComponent,
    getNextSpecId,
    fetchPurchaseOrder,
    showSnackNotificationAction,
    formikActions,
    revisionValue,
    isSaveAndNext,
  });

  useEffect(() => {
    initQuickEditSpecForm({
      specId,
      projectId,
      clientId,
      dataComponentId,
      selectedVendorId: spec?.vendorId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initQuickEditSpecForm, specId, projectId, clientId, dataComponentId]);

  useEffect(
    () => findPO(initDataComponent, performFindRequest, purchaseOrderId),
    [initDataComponent, performFindRequest, purchaseOrderId]
  );

  const handleSubmit = useCallback(
    (data, formikActions) => {
      submit(cloneDataComponentId, data, spec, revisionValue, quickUpdateSpec);
      setFormikActions(formikActions);
    },
    [cloneDataComponentId, spec, revisionValue, quickUpdateSpec]
  );

  const handleSubmitWithConfirmation = useHandleSubmitWithConfirmation({
    purchaseOrder,
    spec,
    openModalDialog,
    openQuickEditModalAction,
    dataComponentId,
    clientId,
    getNextSpecId,
    handleSubmit,
    isSaveAndNextRef,
  });

  const handleCancel = useCallback(() => {
    closeModalDialog();
    if (isSaveAndNext) setReload(dataComponentId, true);
    destroyDataComponent(`${dataComponentId}Clone`);
  }, [
    isSaveAndNext,
    setReload,
    dataComponentId,
    destroyDataComponent,
    closeModalDialog,
  ]);

  const isProjectClosed = useIsProjectClosed();

  return (
    <LabeledTextContext.Provider
      value={isPOChangeDisabled(purchaseOrder) || isProjectClosed}
    >
      <LoaderContext.Provider value={{ loading }}>
        <QuickEditSpecForm
          spec={spec}
          onSubmit={handleSubmitWithConfirmation}
          setSaveAndNext={setSaveAndNext}
          setRef={setRef}
          disableNextButton={!nextSpecId}
          revisionReference={`specs.${_get(spec, "id")}`}
          onCancel={handleCancel}
          loadingSave={loadingSave}
          listeners={[cloneDataComponentId, dataComponentId]}
        />
      </LoaderContext.Provider>
    </LabeledTextContext.Provider>
  );
};

QuickEditSpecContainer.propTypes = {
  spec: propTypes.spec.isRequired,
  nextSpec: propTypes.spec,
  purchaseOrder: propTypes.purchaseOrder,
  closeModalDialog: PropTypes.func.isRequired,
  openModalDialog: PropTypes.func.isRequired,
  initDataComponent: PropTypes.func.isRequired,
  destroyDataComponent: PropTypes.func.isRequired,
  openQuickEditModalAction: PropTypes.func.isRequired,
  quickUpdateSpec: PropTypes.func.isRequired,
  setReload: PropTypes.func.isRequired,
  performFindRequest: PropTypes.func.isRequired,
  initQuickEditSpecForm: PropTypes.func.isRequired,
  cloneDataComponent: PropTypes.object.isRequired,
  clientId: PropTypes.string.isRequired,
  purchaseOrderId: PropTypes.string,
  specId: PropTypes.string.isRequired,
  projectId: PropTypes.string,
  nextSpecId: PropTypes.string,
  getNextSpecId: PropTypes.func.isRequired,
  dataComponentId: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  revisionValue: PropTypes.shape({
    isActive: PropTypes.bool,
    activities: PropTypes.arrayOf(propTypes.revisionActivities),
  }),
  loadingSave: PropTypes.bool,
  fetchPurchaseOrder: PropTypes.func,
  initQuickEditSpec: PropTypes.func,
  cloneDataComponentId: PropTypes.string,
  showSnackNotificationAction: PropTypes.func.isRequired,
  // BW-7011 fix 4: set by openQuickEditModalAction when this instance is a reopen from the
  // spec category confirmation dialog, to carry Save & Next across the remount.
  initialSaveAndNext: PropTypes.bool,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withPORevision(QuickEditSpecContainer));
